/**
 * ThemeManager - Factory for creating themed UI components
 *
 * Usage:
 *   import { themeManager } from './themes/ThemeManager.js';
 *   const knob = themeManager.createKnob(container, { min: 0, max: 100, value: 50 });
 *   knob.onChange(value => console.log(value));
 */

// Import themes synchronously (always available)
import * as BasicTheme from './basic/index.js';
import * as SkeuomorphicTheme from './skeuomorphic/index.js';

// Theme registry
const themes = {
    basic: BasicTheme,
    skeuomorphic: SkeuomorphicTheme
};

class ThemeManager {
    constructor() {
        this.currentTheme = 'skeuomorphic';
    }

    /**
     * Register a new theme
     * @param {string} name
     * @param {Object} themeModule
     */
    registerTheme(name, themeModule) {
        themes[name] = themeModule;
    }

    /**
     * Load additional themes dynamically
     * @param {string} themeName
     * @returns {Promise}
     */
    async loadTheme(themeName) {
        if (themes[themeName]) return themes[themeName];

        // All themes are now loaded synchronously
        console.warn(`Theme '${themeName}' not found in registry`);
        return null;
    }

    /**
     * Get list of available themes
     * @returns {string[]}
     */
    getAvailableThemes() {
        return Object.keys(themes);
    }

    /**
     * Get current theme name
     * @returns {string}
     */
    getTheme() {
        return this.currentTheme;
    }

    /**
     * Set active theme
     * @param {string} themeName
     */
    setTheme(themeName) {
        if (!themes[themeName]) {
            console.warn(`Theme '${themeName}' not found, falling back to 'basic'`);
            themeName = 'basic';
        }
        this.currentTheme = themeName;
        // Emit event for UI rebuild if needed
        window.dispatchEvent(new CustomEvent('themechange', { detail: { theme: themeName } }));
    }

    /**
     * Get the current theme module
     * @returns {Object}
     */
    _getTheme() {
        const theme = themes[this.currentTheme];
        if (!theme) {
            console.error(`Theme '${this.currentTheme}' not loaded!`);
            return themes.basic || {};
        }
        return theme;
    }

    /**
     * Create a knob component
     * @param {HTMLElement} container
     * @param {import('./IComponents.js').KnobOptions} options
     * @returns {import('./IComponents.js').IKnob}
     */
    createKnob(container, options) {
        const theme = this._getTheme();
        if (!theme.createKnob) {
            throw new Error(`Theme '${this.currentTheme}' does not implement createKnob`);
        }
        return theme.createKnob(container, options);
    }

    /**
     * Create a slider component
     * @param {HTMLElement} container
     * @param {import('./IComponents.js').SliderOptions} options
     * @returns {import('./IComponents.js').ISlider}
     */
    createSlider(container, options) {
        const theme = this._getTheme();
        if (!theme.createSlider) {
            throw new Error(`Theme '${this.currentTheme}' does not implement createSlider`);
        }
        return theme.createSlider(container, options);
    }

    /**
     * Create a step button component
     * @param {HTMLElement} container
     * @param {import('./IComponents.js').StepButtonOptions} options
     * @returns {import('./IComponents.js').IStepButton}
     */
    createStepButton(container, options) {
        const theme = this._getTheme();
        if (!theme.createStepButton) {
            throw new Error(`Theme '${this.currentTheme}' does not implement createStepButton`);
        }
        return theme.createStepButton(container, options);
    }

    /**
     * Create a select component
     * @param {HTMLElement} container
     * @param {import('./IComponents.js').SelectOptions} options
     * @returns {import('./IComponents.js').ISelect}
     */
    createSelect(container, options) {
        const theme = this._getTheme();
        if (!theme.createSelect) {
            throw new Error(`Theme '${this.currentTheme}' does not implement createSelect`);
        }
        return theme.createSelect(container, options);
    }

    /**
     * Create an LED component
     * @param {HTMLElement} container
     * @param {Object} options
     * @returns {import('./IComponents.js').ILED}
     */
    createLED(container, options) {
        const theme = this._getTheme();
        if (!theme.createLED) {
            throw new Error(`Theme '${this.currentTheme}' does not implement createLED`);
        }
        return theme.createLED(container, options);
    }
}

// Singleton instance
export const themeManager = new ThemeManager();
